import { bind, classNames, Component, createRef, effect, h, OmiProps, signal, tag } from "omi";
import { Router } from "@vaadin/router";

import { useLayoutContainer } from "@/components/base/nw-draggable-panel/hooks.ts";
import { AuthEvents } from "@/components/nw-auth-manager/events";
import { COMMUNITY_LINK } from "@/globals/links";
import { ErrorService } from "@/services/errorService";
import { NWAvatar, NWButton, NWIcon, NWTooltip, openProModal } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { ToolbarItem } from "./components/toolbarItem";
import { NWToolbarViewManager } from "./views/view-manager";

type Controlled = {
  type: "controlled";
  show: boolean;
  withBackground?: boolean;
};

type Uncontrolled = {
  type: "uncontrolled";
  withBackground?: boolean;
};

type Props = Controlled | Uncontrolled;

@tag("nw-toolbar")
export class NWToolbar extends Component<Props> {
  static css = [tailwind];

  private layoutContainer = useLayoutContainer();
  private items: ToolbarItem[] = [
    {
      type: "button",
      icon: "grid",
      name: "Portfolio",
      color: "gray",
      iconOnly: true,
      active: () => Store.app.isRouteActive("portfolio"),
      onClick: () => {
        import("@/services/analyticsService").then(({ useAnalyticsService }) => {
          useAnalyticsService().trackNavItemClicked("Portfolio");
        });

        if (Store.user.isLoggedIn.value) {
          if (Store.app.isRouteActive("portfolio")) {
            Router.go("/");
          } else {
            Router.go("/portfolio");
          }
        } else {
          if (Store.app.isRouteActive("portfolio")) {
            window.location.assign("/");
          } else {
            window.location.assign("/portfolio");
          }
        }
      },
    },
    {
      type: "button",
      icon: "search",
      name: "Projects",
      color: "gray",
      iconOnly: true,
      onClick: () => {
        import("@/services/analyticsService").then(({ useAnalyticsService }) => {
          useAnalyticsService().trackNavItemClicked("Projects");
        });

        if (Store.features.hasFeature("projects.navV2")) {
          Store.app.showToolbarView("directory");
          import("@/services/analyticsService").then(({ useAnalyticsService }) => {
            useAnalyticsService().trackSidebarOpen("toolbar");
          });
        } else {
          Store.app.toggleNav(!Store.app.isNavOpen.value, "toolbar");
        }
      },
      active: () => Store.app.isNavOpen.value,
    },
    {
      type: "link",
      name: "Stuck? Ask a question",
      icon: "message-question-circle",
      href: COMMUNITY_LINK,
      onClick: () => {
        import("@/services/analyticsService").then(({ useAnalyticsService }) => {
          useAnalyticsService().trackNavItemClicked("Stuck? Ask a question");
        });
      },
    },
  ];
  private afterScrollHideLimit = 1200;

  private lastScrollTop = signal(0);
  private isRevealed = signal(true);
  private isPinned = signal(false);
  private isHovered = signal(false);

  private toolbarRef = createRef<HTMLDivElement>();

  installed() {
    if (Store.user.isLoggedIn.value) {
      this.layoutContainer.onRegister(ref => ref.addEventListener("scroll", this.handleOnScroll, { passive: true }));

      this.isPinned.value = Store.user.preferences.isToolbarPinned?.value ?? true;
    }
  }

  uninstall() {
    if (Store.user.isLoggedIn.value) {
      this.layoutContainer.ref.value?.removeEventListener("scroll", this.handleOnScroll);
    }
  }

  receiveProps(props: OmiProps<Props> | Props) {
    return !!isControlled(props);
  }

  @bind
  ready() {
    setTimeout(() => {
      try {
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;
        if (scrollY <= this.afterScrollHideLimit) {
          this.isRevealed.value = true;
        }
      } catch (e) {
        ErrorService.report(e);
      }
    }, 200);

    effect(() => {
      const viewingPortfolio = Store.app.isRouteActive("portfolio");
      if (viewingPortfolio && this.isRevealed.value) {
        setTimeout(() => {
          this.isRevealed.value = false;
        }, 10000);
      }
    });
  }

  @bind
  private handleOnScroll() {
    if (Store.app.isRouteActive("portfolio")) {
      return;
    }

    const scrollY =
      window.pageYOffset || document.documentElement.scrollTop || this.layoutContainer.ref.value!.scrollTop;

    if (scrollY > this.afterScrollHideLimit) {
      this.isRevealed.value = scrollY < this.lastScrollTop.value;
      this.lastScrollTop.value = scrollY <= 0 ? 0 : scrollY;
    } else {
      this.isRevealed.value = true;
    }
  }

  @bind
  private handleBackgroundClick() {
    Store.app.showToolbarView(undefined);
  }

  @bind
  private handleTogglePinned() {
    const targetState = !this.isPinned.value;
    this.isPinned.value = targetState;

    if (Store.project.id.value) {
      import("@/services/analyticsService").then(({ useAnalyticsService }) => {
        useAnalyticsService().trackPinToggled(
          Store.project.id.value!,
          "bubble-bar",
          targetState ? "pinned" : "unpinned",
        );
      });
    }

    Store.user.preferences.updatePreferences({ isToolbarPinned: targetState });
  }

  @bind
  private handleSignIn() {
    const loginEvent = new Event(AuthEvents.LoginClicked);
    dispatchEvent(loginEvent);
  }

  @bind
  private handleHomeClick() {
    return window.location.assign("/");
  }

  @bind
  private handleOpenPaywall() {
    openProModal("nav_explore_your_potential_button");
  }

  @bind
  private handleMouseEnter() {
    setTimeout(() => {
      this.isHovered.value = true;
    }, 200);
  }

  @bind
  private handleMouseLeave() {
    setTimeout(() => {
      this.isHovered.value = false;
    }, 200);
  }

  render({ withBackground = true, ...props }: Props) {
    const isControlledToolbar = isControlled(props);
    const hasActiveView = Store.app.activeToolbarView.value != null;
    const isRevealed = isControlledToolbar
      ? props.show
      : this.isPinned.value || this.isRevealed.value || Store.app.isNavOpen.value || hasActiveView;
    const shouldExpandToolbar = this.isHovered.value || (isControlledToolbar && props.show) || hasActiveView;
    const shouldAllowPinning = Store.user.isLoggedIn.value;
    const showUpgradeButton = !Store.user.isPro();

    return (
      <>
        {withBackground && (
          <div
            onClick={this.handleBackgroundClick}
            className={classNames(
              "fixed top-0 left-0 right-0 bottom-0 bg-black/10 backdrop-blur-sm z-[500] transition-opacity duration-300 ease-in-out",
              {
                "opacity-0 pointer-events-none": !hasActiveView,
                "opacity-100": hasActiveView,
              },
            )}
          />
        )}

        <div
          class={classNames(
            "fixed bottom-0 left-1/2 -translate-x-1/2 h-24 sm:h-24 flex flex-col space-y-1 justify-center py-4 z-[501] w-full sm:min-w-[560px] sm:w-full sm:max-w-[816px] transition-all ease-in-out duration-300",
            { "pointer-events-none": isRevealed, group: !isRevealed },
          )}
        >
          <div
            class={classNames(
              "group-hover:translate-y-0 pointer-events-auto transition-transform ease-in-out duration-300 fixed bottom-4 left-1/2 -translate-x-1/2 flex flex-col justify-center items-center space-y-2",
              {
                "translate-y-0": isRevealed,
                "translate-y-[300%]": !isRevealed,
              },
            )}
          >
            <div
              class={classNames(
                "transition-all ease-in-out duration-300 group-hover:translate-y-0 flex items-center justify-center",
              )}
            >
              <NWToolbarViewManager />
            </div>

            <div
              className="relative group w-full"
              onMouseEnter={this.handleMouseEnter}
              onMouseLeave={this.handleMouseLeave}
            >
              {showUpgradeButton && (
                <button
                  class="absolute bg-leather text-white text-sm font-bold py-1 px-5 rounded-t-2xl h-8 -top-8 left-1/2 -translate-x-1/2 w-fit text-nowrap flex items-center space-x-2 hover:bg-gray-900 transition-all ease-in-out"
                  onClick={this.handleOpenPaywall}
                >
                  <span>Explore your potential</span>
                  <NWIcon name="arrow-up-right" width={20} height={20} />
                </button>
              )}

              {shouldAllowPinning ? (
                <button
                  class={classNames(
                    "rounded-3xl p-1.5 shrink-0 flex items-center justify-center border bg-white absolute top-0 translate-x-[40%] -translate-y-[20%] delay-75 right-0 transition-all group-hover:opacity-100 opacity-0 hover:bg-secondary",
                    { hidden: hasActiveView },
                  )}
                  onClick={this.handleTogglePinned}
                >
                  <NWIcon
                    name="pin"
                    width={16}
                    height={16}
                    className={classNames("transition-all", {
                      "text-leather": this.isPinned.value,
                      "text-tertiary rotate-45": !this.isPinned.value,
                    })}
                  />
                </button>
              ) : (
                <div />
              )}
              <div
                ref={this.toolbarRef}
                className={classNames(
                  "group pointer-events-auto bg-white py-4 px-8 border border-gray-200 rounded-3xl overflow-hidden transition-[width] duration-100",
                  "sm:grid sm:grid-cols-[auto_1fr_auto] flex items-center justify-center",
                  {
                    "w-64": !hasActiveView,
                    "w-full max-w-[90%] mx-auto md:max-w-2xl": hasActiveView,
                    "sm:w-[40rem]": hasActiveView && Store.app.activeToolbarView.value === "directory",
                    "sm:w-[30rem]":
                      shouldExpandToolbar || (hasActiveView && Store.app.activeToolbarView.value === "user"),
                  },
                )}
              >
                <div
                  className={classNames("items-center hidden sm:flex transition-all duration-100 ease-in-out", {
                    "sm:w-0 opacity-0": !shouldExpandToolbar,
                    "sm:opacity-100 sm:delay-100": shouldExpandToolbar,
                    "sm:justify-start": !Store.user.isLoggedIn.value,
                    "sm:!w-16": !Store.user.isLoggedIn.value && shouldExpandToolbar,
                    "sm:w-9 sm:h-9 justify-center": Store.user.isLoggedIn.value && shouldExpandToolbar,
                  })}
                >
                  <img
                    src="/static/nextwork-icon.svg"
                    alt="NextWork Logo"
                    className={classNames("shrink-0 black cursor-pointer transition-transform hover:scale-110", {
                      "w-9 h-[2.375rem]": !Store.user.isLoggedIn.value,
                      "w-8 h-8": Store.user.isLoggedIn.value,
                    })}
                    width={Store.user.isLoggedIn.value ? 32 : 38}
                    height={Store.user.isLoggedIn.value ? 32 : 38}
                    onClick={this.handleHomeClick}
                  />
                </div>

                <div className="flex items-center justify-center space-x-3">
                  {this.items.map(item => (
                    <ToolbarItem {...item} />
                  ))}
                </div>

                <div
                  className={classNames("items-center hidden sm:flex transition-all duration-100 ease-in-out", {
                    "sm:w-0 opacity-0": !shouldExpandToolbar,
                    "sm:opacity-100 sm:delay-100": shouldExpandToolbar,
                    "sm:overflow-x-hidden sm:justify-start": !Store.user.isLoggedIn.value,
                    "sm:!w-16": !Store.user.isLoggedIn.value && shouldExpandToolbar,
                    "sm:w-9 sm:h-9 justify-center": Store.user.isLoggedIn.value && shouldExpandToolbar,
                  })}
                >
                  {Store.user.isLoggedIn.value ? (
                    <NWTooltip content="My profile" placement="top" delay={[400, 0]}>
                      <NWAvatar withProBorder onClick={() => Store.app.showToolbarView("user")}></NWAvatar>
                    </NWTooltip>
                  ) : (
                    <NWButton onClick={this.handleSignIn} color="brand" size="fit">
                      Login
                    </NWButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

function isControlled(props: Props): props is Controlled {
  return (props as Controlled).type === "controlled";
}
